import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, TextField, Alert, Link, Box } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { confirmSignUp, resendSignUpCode } from "aws-amplify/auth";
import { useAuth } from "../libs/AuthContext";
import LoaderButton from "../components/LoaderButton";
import FormPage from "../components/FormPage";

const schema = Yup.object({
  confirmationCode: Yup.string().required("Confirmation code is required")
});

export default function VerifyEmail() {
  const { emailAddress } = useAuth();
  const navigate = useNavigate();

  const initialEmail = (() => {
    if (emailAddress) {
      try { localStorage.setItem("emailAddress", emailAddress); } catch (e) { /* ignore */ }
      return emailAddress;
    }
    try { return localStorage.getItem("emailAddress") ?? ""; } catch (e) { return ""; }
  })();

  const [email] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const resendConfirmation = async () => {
    setAlertMessage("");
    setInfoMessage("");
    try {
      await resendSignUpCode({ username: email });
      setInfoMessage("New code sent. Please check your email.");
    } catch (e) {
      setAlertMessage(e?.message ?? "Resend failed");
    }
  };

  const handleSubmit = async (event) => {
    setIsLoading(true);
    setAlertMessage("");
    setInfoMessage("");
    try {
      await confirmSignUp({ username: email, confirmationCode: event.confirmationCode });
      navigate("/");
    } catch (e) {
      setAlertMessage(e?.message ?? "Verification failed");
    }
    setIsLoading(false);
  };

  return (
    <FormPage
      title="Verify your email"
      subtitle="We sent a confirmation code to your inbox. Enter it below to activate your account."
    >
      {alertMessage ? (
        <Alert severity="error" onClose={() => setAlertMessage("")}>{alertMessage}</Alert>
      ) : null}
      {infoMessage ? (
        <Alert severity="success" onClose={() => setInfoMessage("")}>{infoMessage}</Alert>
      ) : null}
      <Formik
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{ confirmationCode: "" }}
      >
        {({ handleSubmit, handleChange, values, errors }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label="Email"
                value={email}
                disabled
                autoComplete="off"
              />
              <TextField
                name="confirmationCode"
                label="Confirmation code"
                value={values.confirmationCode}
                onChange={handleChange}
                error={!!errors.confirmationCode}
                helperText={errors.confirmationCode}
                autoComplete="one-time-code"
                inputMode="numeric"
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                autoFocus
              />
              <Box sx={{ textAlign: "right", mt: -1 }}>
                <Link component="button" type="button" onClick={resendConfirmation} variant="body2">
                  Resend confirmation code
                </Link>
              </Box>
              <LoaderButton
                type="submit"
                isLoading={isLoading}
                text="Verify"
                loadingText="Verifying…"
                fullWidth
                size="large"
              />
            </Stack>
          </Box>
        )}
      </Formik>
    </FormPage>
  );
}
